import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { useScopedTranslation } from 'app/hooks/i18n';
import { Event, track } from 'app/lib/analytics';
import { useUser } from 'app/network/user';
import type { NavigationScreen } from 'app/types/navigation';
import React, { useEffect } from 'react';
import { RefreshControl, View } from 'react-native';
import { Text } from 'app/design/primitives/text';
import { useOneLoan } from 'app/network/loan';
import { useNavigationOptions } from 'app/hooks/navigation';

import { useDeviceWidth } from 'app/hooks/use-device-size';
import { Separator } from 'app/design/primitives/text-divider';
import { userInfoAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import { History } from './components/history';
import { Accessory } from './components/accessory';
import { Head } from './components/head';
import { Details } from './components/details';
import { getLoanInfos } from './functions';

import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { usePageTitle } from 'app/utils/web-helpers';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

function LoanDetailsHeaderTitle({ title }: { title: string }) {
  return (
    <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
      {title}
    </Text>
  );
}

const LoanDetailsScreenContent: NavigationScreen<'one-loan'> = ({ navigation, route }) => {
  const { t } = useScopedTranslation();
  const deviceWidth = useDeviceWidth();
  const userQuery = useUser();
  const userInfo = useAtomValue(userInfoAtom);
  const loanQuery = useOneLoan(route.params.id);
  const loan = loanQuery.data;
  const currentUser = userQuery.data ?? userInfo ?? undefined;

  const { loanType } = (() => {
    return getLoanInfos(loan, currentUser);
  })();

  const isService = loan?.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan?.listing);
  const isSell = loan?.listing.transactionMode === TransactionMode.sell;

  const tSuffix = isSell ? 'sell' : isService ? 'service' : isDonation ? 'donation' : loanType;

  const headerTitle = () => <LoanDetailsHeaderTitle title={t(`title.${tSuffix}`)} />;

  useNavigationOptions(navigation, {
    headerBackVisible: false,
    headerTitle,
  });

  const setPageTitle = usePageTitle('categories');
  useEffect(
    () => setPageTitle(`${t(`title.${tSuffix}`)} - ${loan?.listing.item.name ?? '...'}`),
    [loan?.listing.item.name, setPageTitle, t, tSuffix],
  );

  useEffect(() => {
    track(Event.LoanDetailsOpened, { loan_id: route.params.id });

    return () => {
      track(Event.LoanDetailsClosed, { loan_id: route.params.id });
    };
  }, [route.params.id]);

  useRegisterLoading(loanQuery.isPending, 'one-loan-screen');
  useRegisterLoading(userQuery.isPending, 'one-loan-screen');

  return (
    <Page
      accessory={loan && currentUser ? <Accessory loan={loan} user={currentUser} /> : null}
      refreshControl={<RefreshControl onRefresh={() => void loanQuery.refetch()} refreshing={loanQuery.isRefetching} />}
    >
      <Column gap="spacing.general.x4">
        <Head loan={loan} user={currentUser} />

        <View style={{ marginLeft: -15, width: deviceWidth }}>
          <Separator color={theme.colors['surface.tertiary-neutral']} height={10} />
        </View>

        <Details loan={loan} user={currentUser} />

        <View style={{ marginLeft: -15, width: deviceWidth }}>
          <Separator color={theme.colors['surface.tertiary-neutral']} height={10} />
        </View>

        <History loan={loan} user={currentUser} />
      </Column>
    </Page>
  );
};

export const LoanDetailsScreen = withOptionalMetrics('LoanDetailsScreen', withErrorBoundary(LoanDetailsScreenContent));
